'use client';

import type { PrV2CommentLink } from '@/lib/pr-v2/comment-anchors';
import type { PrV2CommentTarget, PrV2Tab } from '@/lib/pr-v2/tabs';
import type {
  PrV2CommentResponse,
  PrV2DiscussionResponse,
  PrV2ViewerMode,
} from '@/lib/pr-v2/types';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { memo, useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';

import { useOpenPrV2Comment } from '../hooks/use-open-pr-v2-comment';
import { useReportPrOpenTiming } from '../hooks/use-report-pr-open-timing';
import { PrV2CommentsControls } from './comments/pr-v2-comments-controls';
import { PrV2FileTreeToolbarSkeleton } from './shared/skeletons/pr-v2-file-tree-skeleton';
import {
  PrV2ChangesPaneSkeleton,
  PrV2DiscussionPaneSkeleton,
} from './shared/skeletons/pr-v2-loading-sections';

import { Tabs, TabsContent, TabsList, UnderlineTabsTrigger } from '@/components/ui/tabs';
import { fetchPrV2Discussion } from '@/data/v2/pr/fetch-pr-v2';
import { PR_V2_STANDARD_STALE_TIME_MS } from '@/data/v2/pr/query-config';
import { prV2DiscussionQueryKey } from '@/data/v2/pr/query-keys';
import { usePrV2Comments } from '@/data/v2/pr/use-pr-v2-comments';
import { usePrV2CubicReviewSummary } from '@/data/v2/pr/use-pr-v2-cubic-review-summary';
import { usePrV2Discussion } from '@/data/v2/pr/use-pr-v2-discussion';
import { useIdleCallback } from '@/hooks/use-idle-callback';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  parsePrV2CommentLink,
  PR_V2_COMMENT_ID_QUERY_PARAM,
  replacePrV2PageUrl,
  setPrV2CommentLink,
} from '@/lib/pr-v2/comment-anchors';
import { PR_V2_TAB_HANDOFF_EVENT } from '@/lib/pr-v2/tab-handoff';
import {
  parsePrV2Tab,
  PR_V2_TAB_OPTIONS,
  PR_V2_TAB_QUERY_PARAM,
  PR_V2_TABS,
} from '@/lib/pr-v2/tabs';
import { PR_V2_VIEWER_MODE } from '@/lib/pr-v2/types';
import { getPrV2ActiveFileStoreKey } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { useMarkPrV2ContentPainted } from '@/lib/stores/navigation/pr-v2-content-paint-store';
import { cn } from '@/lib/utils';

const PrV2DiscussionTimeline = dynamic(
  () =>
    import('./discussion/pr-v2-discussion-timeline').then(
      (module) => module.PrV2DiscussionTimeline,
    ),
  {
    loading: () => <PrV2DiscussionPaneSkeleton />,
  },
);

const PrV2PierreDiff = dynamic(
  () => import('./pr-v2-pierre-diff').then((module) => module.PrV2PierreDiff),
  {
    loading: () => <PrV2ChangesPaneSkeleton />,
  },
);

function preloadPrV2TabPaneModules() {
  // Warm both code-split tab panes so the first tab switch never hits the
  // dynamic() loading skeleton. Imports are idempotent; the active tab's
  // module is already loading, so this only fetches the inactive one.
  // Discussion DATA is intentionally not prefetched here: the API schedules
  // GitHub hydration syncs for cold PRs, so data stays on hover intent
  // (prefetchDiscussionTab).
  void import('./discussion/pr-v2-discussion-timeline');
  void import('./pr-v2-pierre-diff');
}

const EMPTY_DISCUSSION: PrV2DiscussionResponse = {
  syncState: 'ready',
  timelineItems: [],
  commitCheckSuites: [],
  reactions: {},
  cachedAt: null,
};

const PR_V2_TAB_HISTORY_PUSH = 'push' as const;
const PR_V2_TAB_HISTORY_REPLACE = 'replace' as const;

function readPrV2CommentLinkFromWindow(): PrV2CommentLink | null {
  return parsePrV2CommentLink(new URL(window.location.href));
}

// The same comment can be linked on either tab.
function getPrV2CommentLinkKey(link: PrV2CommentLink | null): string | null {
  return link ? `${link.tab}:${link.commentId}` : null;
}

// Read the link as the URL will once nuqs writes this tab; anchorless links take the tab param.
function readPrV2CommentLinkFromWindowOnTab(tab: PrV2Tab): PrV2CommentLink | null {
  const url = new URL(window.location.href);
  url.searchParams.set(PR_V2_TAB_QUERY_PARAM, tab);
  return parsePrV2CommentLink(url);
}

function clearPrV2CommentLinkFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(PR_V2_COMMENT_ID_QUERY_PARAM);
  url.hash = '';
  replacePrV2PageUrl(url);
}

function hasDiscussionTabIntent(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const url = new URL(window.location.href);
  return (
    parsePrV2Tab(url.searchParams.get(PR_V2_TAB_QUERY_PARAM)) === PR_V2_TABS.DISCUSSION ||
    parsePrV2CommentLink(url)?.tab === PR_V2_TABS.DISCUSSION
  );
}

type PrV2TabsProps = {
  owner: string;
  repo: string;
  number: string;
  viewerMode: PrV2ViewerMode;
  headSha: string | null;
  baseSha: string | null;
  headRef?: string | null;
  isMerged?: boolean;
  installationId?: number | null;
  /** The base branch's name, or its short SHA when the name is unknown. */
  baseLabel: string;
};

export const PrV2Tabs = memo(
  ({
    owner,
    repo,
    number,
    viewerMode,
    headSha,
    baseSha,
    headRef = null,
    isMerged = false,
    installationId = null,
    baseLabel,
  }: PrV2TabsProps) => {
    const [activeTabState, setTabQueryState] = useQueryState(
      PR_V2_TAB_QUERY_PARAM,
      parseAsStringLiteral(PR_V2_TAB_OPTIONS).withDefault(PR_V2_TABS.CHANGES).withOptions({
        history: PR_V2_TAB_HISTORY_REPLACE,
        clearOnDefault: true,
        scroll: false,
      }),
    );
    const activeTab = activeTabState as PrV2Tab;
    // Next gives useSearchParams() a new identity whenever its URL changes, the hash included.
    const searchParams = useSearchParams();
    // The comment link (tab and comment) the URL carried when last read, so a rewrite that keeps it
    // does not reopen it.
    const linkedCommentKeyRef = useRef<string | null>(null);
    const queryClient = useQueryClient();
    const initialTabResolvedRef = useRef(false);
    const commentsQuery = usePrV2Comments(owner, repo, number);
    const cubicReviewSummaryQuery = usePrV2CubicReviewSummary(owner, repo, number, {
      enabled: viewerMode === PR_V2_VIEWER_MODE.MEMBER,
    });
    // Shared with PrV2PierreDiff so its reaction-counter discussion subscription defers on the
    // Changes tab exactly like this one (see preloadPrV2TabPaneModules for the data rationale).
    const isDiscussionDataEnabled = activeTab === PR_V2_TABS.DISCUSSION || hasDiscussionTabIntent();
    const discussionQuery = usePrV2Discussion(owner, repo, number, {
      enabled: isDiscussionDataEnabled,
    });
    const comments = commentsQuery.data ?? [];
    const severityByCommentId = cubicReviewSummaryQuery.data?.severityByCommentId;
    const discussion = discussionQuery.data ?? EMPTY_DISCUSSION;
    const [fileTreeToolbarHost, setFileTreeToolbarHost] = useState<HTMLDivElement | null>(null);
    const isMobile = useIsMobile();
    const [isDiscussionTimelineMounted, setIsDiscussionTimelineMounted] = useState(false);
    const isDiscussionReady = isDiscussionTimelineMounted && !discussionQuery.isLoading;
    const { requestComment, abandonCommentsOutside, openingDiscussionCommentId } =
      useOpenPrV2Comment({
        activeTab,
        isDiscussionReady,
      });
    // On the Discussion tab the timeline is the PR's content, so its readiness releases the
    // deferred startup requests instead of their 5 s backstop.
    useMarkPrV2ContentPainted(
      getPrV2ActiveFileStoreKey(owner, repo, number),
      activeTab === PR_V2_TABS.DISCUSSION && isDiscussionReady,
    );
    useReportPrOpenTiming({ owner, repo, number, activeTab, installationId });

    useIdleCallback(preloadPrV2TabPaneModules, []);

    const setActiveTab = useCallback(
      (
        value: PrV2Tab,
        history: typeof PR_V2_TAB_HISTORY_PUSH | typeof PR_V2_TAB_HISTORY_REPLACE,
      ) => {
        abandonCommentsOutside(value);

        // Leaving the tab the URL's comment link points at drops the link. Read it before the
        // switch: a Changes link has no `tab` param, so after the switch it would read as a link to
        // the new tab. Clear it once nuqs has written the tab (see replacePrV2PageUrl).
        const link = readPrV2CommentLinkFromWindow();
        // Until cleared, an already opened link can read as a link to the new tab; do not reopen it.
        if (
          link &&
          link.tab !== value &&
          getPrV2CommentLinkKey(link) === linkedCommentKeyRef.current
        ) {
          linkedCommentKeyRef.current = getPrV2CommentLinkKey(
            readPrV2CommentLinkFromWindowOnTab(value),
          );
        }
        void Promise.resolve(setTabQueryState(value, { history, scroll: false })).then(() => {
          if (link && link.tab !== value) {
            clearPrV2CommentLinkFromUrl();
          }
        });
      },
      [abandonCommentsOutside, setTabQueryState],
    );

    const openComment = useCallback(
      (target: PrV2CommentTarget) => {
        requestComment(target);
        setActiveTab(target.tab, PR_V2_TAB_HISTORY_REPLACE);
      },
      [requestComment, setActiveTab],
    );

    const handleSeeCommentInChanges = useCallback(
      (comment: PrV2CommentResponse) => {
        openComment({
          tab: PR_V2_TABS.CHANGES,
          commentId: comment.githubCommentId,
          filename: comment.path ?? undefined,
        });
      },
      [openComment],
    );

    useEffect(() => {
      if (typeof window === 'undefined' || initialTabResolvedRef.current) {
        return;
      }

      initialTabResolvedRef.current = true;

      const url = new URL(window.location.href);
      if (parsePrV2Tab(url.searchParams.get(PR_V2_TAB_QUERY_PARAM)) === PR_V2_TABS.CHANGES) {
        setActiveTab(PR_V2_TABS.CHANGES, PR_V2_TAB_HISTORY_REPLACE);
      }
    }, [setActiveTab]);

    const openLinkedComment = useEffectEvent(() => {
      const url = new URL(window.location.href);
      const link = parsePrV2CommentLink(url);
      const linkedCommentKey = getPrV2CommentLinkKey(link);
      if (linkedCommentKey === linkedCommentKeyRef.current) {
        return;
      }
      linkedCommentKeyRef.current = linkedCommentKey;
      if (!link) {
        return;
      }

      requestComment(link);
      // The URL picks the tab. nuqs drops a tab update made while Next is still writing the
      // navigation that brought the link, so a link that names its comment only by its anchor is
      // written out in full through Next instead (see replacePrV2PageUrl).
      const urlTab =
        parsePrV2Tab(url.searchParams.get(PR_V2_TAB_QUERY_PARAM)) ?? PR_V2_TABS.CHANGES;
      if (
        urlTab !== link.tab ||
        url.searchParams.get(PR_V2_COMMENT_ID_QUERY_PARAM) !== link.commentId
      ) {
        setPrV2CommentLink(url, link);
        replacePrV2PageUrl(url);
      }
    });

    useEffect(() => {
      // Copied and notification links must land on their comment after a reload, a new tab or an
      // auth redirect, while the tabs lazy-mount their content. Runs after every URL change, so a
      // notification click for the PR already open (a router.push to this page) opens its comment
      // too, even when only the hash changes.
      openLinkedComment();
    }, [searchParams]);

    useEffect(
      () => () => {
        // A page shown again (React Activity) or mounted again opens its link again. Opening the
        // same link twice (Strict Mode) is harmless.
        linkedCommentKeyRef.current = null;
      },
      [],
    );

    useEffect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const handleTabHandoff = (event: Event) => {
        const detail =
          event instanceof CustomEvent && typeof event.detail === 'object' && event.detail
            ? event.detail
            : null;
        const nextTab = parsePrV2Tab(
          detail && 'tab' in detail && typeof detail.tab === 'string' ? detail.tab : null,
        );
        if (!nextTab) {
          return;
        }

        if (detail && 'commentId' in detail && typeof detail.commentId === 'string') {
          openComment({
            tab: nextTab,
            commentId: detail.commentId,
            filename: typeof detail.filename === 'string' ? detail.filename : undefined,
            targetHeadSha:
              typeof detail.targetHeadSha === 'string' ? detail.targetHeadSha : undefined,
          });
          return;
        }

        setActiveTab(nextTab, PR_V2_TAB_HISTORY_REPLACE);
      };

      window.addEventListener(PR_V2_TAB_HANDOFF_EVENT, handleTabHandoff);
      return () => {
        window.removeEventListener(PR_V2_TAB_HANDOFF_EVENT, handleTabHandoff);
      };
    }, [openComment, setActiveTab]);

    const prefetchDiscussionTab = () => {
      // Lazy-load discussion UI and data on intent (hover/focus) for snappier tab switch.
      void import('./discussion/pr-v2-discussion-timeline');
      void queryClient.prefetchQuery({
        queryKey: prV2DiscussionQueryKey(owner, repo, number),
        queryFn: ({ signal }) => fetchPrV2Discussion(owner, repo, number, signal),
        staleTime: PR_V2_STANDARD_STALE_TIME_MS,
      });
    };

    const tabClassName =
      'h-full min-h-0 px-2 text-zinc-500 hover:text-zinc-100 aria-selected:text-zinc-100 hover:after:bg-zinc-700 aria-selected:after:bg-zinc-100 sm:px-3';
    const prContentClass =
      'col-start-1 col-end-13 max-md:px-3 md:col-start-2 md:col-end-10 2xl:mx-auto 2xl:w-[1000px]';
    const renderReviewTabsList = ({ showFileTreeToolbar }: { showFileTreeToolbar: boolean }) => (
      <div className="grid h-full w-full grid-cols-[minmax(0,1fr)_auto] md:@xl/review-tabs:grid-cols-12 md:@xl/review-tabs:gap-4">
        <div className="col-start-1 col-end-2 row-start-1 flex h-full min-h-9 min-w-0 w-full items-center pl-6 max-md:pl-3 md:@xl/review-tabs:col-start-2 md:@xl/review-tabs:col-end-10 md:@xl/review-tabs:pl-0 2xl:@xl/review-tabs:mx-auto 2xl:@xl/review-tabs:w-[1000px]">
          <TabsList
            data-testid="pr-v2-review-tabs-list"
            className="flex h-full min-h-9 min-w-0 items-center justify-start overflow-x-auto overflow-y-hidden scrollbar-hide rounded-none bg-transparent p-0 md:@xl/review-tabs:overflow-visible"
          >
            <UnderlineTabsTrigger value={PR_V2_TABS.CHANGES} className={tabClassName}>
              Changes
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger
              value={PR_V2_TABS.DISCUSSION}
              className={tabClassName}
              onMouseEnter={prefetchDiscussionTab}
              onFocus={prefetchDiscussionTab}
            >
              Discussion
            </UnderlineTabsTrigger>
          </TabsList>
        </div>
        {viewerMode === PR_V2_VIEWER_MODE.MEMBER ? (
          <div
            className={cn(
              'col-start-2 row-start-1 flex items-center justify-end pl-3 pr-2 max-md:pr-3 md:@xl/review-tabs:col-start-10 md:@xl/review-tabs:col-end-13',
              // On desktop the file tree toolbar follows and sets the space after these controls.
              showFileTreeToolbar && 'md:pr-0',
            )}
          >
            <PrV2CommentsControls
              owner={owner}
              repo={repo}
              number={number}
              onOpenComment={openComment}
            />
          </div>
        ) : null}
      </div>
    );
    const renderReviewSubheader = ({ showFileTreeToolbar }: { showFileTreeToolbar: boolean }) => (
      <div
        data-pr-v2-tabs-list="true"
        data-testid="pr-v2-review-subheader"
        className="sticky top-0 z-30 flex min-h-9 w-full items-stretch border-b border-zinc-800/60 bg-background"
      >
        <div className="@container/review-tabs min-w-0 flex-1">
          {renderReviewTabsList({ showFileTreeToolbar })}
        </div>
        {showFileTreeToolbar ? (
          <div
            ref={setFileTreeToolbarHost}
            data-testid="pr-v2-review-subheader-file-tree-toolbar"
            className="hidden shrink-0 md:block"
          >
            <div
              data-testid="pr-v2-file-tree-toolbar-placeholder"
              className="[&:not(:only-child)]:hidden"
            >
              <PrV2FileTreeToolbarSkeleton />
            </div>
          </div>
        ) : null}
      </div>
    );
    const changesDiffPane = (
      <PrV2PierreDiff
        owner={owner}
        repo={repo}
        number={number}
        isReadOnly={viewerMode === PR_V2_VIEWER_MODE.PUBLIC}
        headSha={headSha}
        baseSha={baseSha}
        headRef={headRef}
        isMerged={isMerged}
        installationId={installationId}
        comments={comments}
        baseLabel={baseLabel}
        severityByCommentId={severityByCommentId}
        // This page's toolbar host is hidden below md; phones keep the floating Files button.
        fileTreeToolbarHost={isMobile ? null : fileTreeToolbarHost}
        discussionDataEnabled={isDiscussionDataEnabled}
      />
    );

    return (
      <section>
        <Tabs
          defaultValue={PR_V2_TABS.CHANGES}
          value={activeTab}
          onValueChange={(nextValue) => {
            const nextTab = parsePrV2Tab(nextValue);
            if (nextTab) {
              setActiveTab(nextTab, PR_V2_TAB_HISTORY_PUSH);
            }
          }}
          className="min-h-screen w-full"
        >
          {/* One tab bar for both tabs, so a tab switch keeps its comment controls mounted. */}
          {renderReviewSubheader({ showFileTreeToolbar: activeTab === PR_V2_TABS.CHANGES })}

          <TabsContent value={PR_V2_TABS.CHANGES} className="mt-0">
            {activeTab === PR_V2_TABS.CHANGES ? changesDiffPane : null}
          </TabsContent>

          <TabsContent value={PR_V2_TABS.DISCUSSION} className="mt-0">
            {activeTab === PR_V2_TABS.DISCUSSION ? (
              <div className="grid w-full grid-cols-12 gap-4">
                <div className={`${prContentClass} space-y-4 pt-4`}>
                  <PrV2DiscussionTimeline
                    owner={owner}
                    repo={repo}
                    number={number}
                    isReadOnly={viewerMode === PR_V2_VIEWER_MODE.PUBLIC}
                    headSha={headSha}
                    baseSha={baseSha}
                    headRef={headRef}
                    isMerged={isMerged}
                    discussion={discussion}
                    comments={comments}
                    severityByCommentId={severityByCommentId}
                    onSeeInChanges={handleSeeCommentInChanges}
                    onMountedChange={setIsDiscussionTimelineMounted}
                    openingCommentId={openingDiscussionCommentId}
                  />
                </div>
              </div>
            ) : null}
          </TabsContent>
        </Tabs>
      </section>
    );
  },
);

PrV2Tabs.displayName = 'PrV2Tabs';
